interface Config {
  apiFilter?: string | null
  scope?: string
  apiQueryParams?: string | null
  apiLinkIncludes?: string | null
}

interface ExtractedParams {
  baseUrl: string
  urlQuery: Record<string, any>
  filter: string
  params: string
  includes: string | null
  readableFacets: string
  facets: Record<string, any>
  countryDataParam: Record<string, boolean>
  scoringParams: Record<string, number>
}

export function extractApiParams(options: {
  baseUrl: string
  route: any
  config: Config
  facets: Record<string, any>
}): ExtractedParams {
  const { baseUrl, route, config, facets } = options
  const urlQuery = route.query

  // Clone to avoid mutating Vuex state
  const clonedFacets = { ...facets }

  // --- dataServices ---
  const dataServicesParam =
    clonedFacets.dataServices?.includes('true') ? '&dataServices=true' : ''
  delete clonedFacets.dataServices

  // --- countryData ---
  let countryDataStr =
    clonedFacets.country?.length > 0 ? '&countryData=true' : ''

  if (clonedFacets.country?.includes('countryData')) {
    clonedFacets.country = clonedFacets.country.filter(
      (item: string) => item !== 'countryData'
    )
  }

  // --- scoring ---
  let scoringStr = ''
  const SCORING_RANGES: Record<string, { minScoring: number; maxScoring: number }> = {
    sufficientScoring: { minScoring: 121, maxScoring: 220 },
    goodScoring:       { minScoring: 221, maxScoring: 350 },
    excellentScoring:  { minScoring: 351, maxScoring: 405 },
  }

  let scoringParams: Record<string, number> = {}
  if (clonedFacets.scoring?.length > 0) {
    if (clonedFacets.scoring.includes('excellentScoring')) {
      scoringStr = '&minScoring=351'
      scoringParams = SCORING_RANGES.excellentScoring
    } else if (clonedFacets.scoring.includes('goodScoring')) {
      scoringStr = '&minScoring=221'
      scoringParams = SCORING_RANGES.goodScoring
    } else if (clonedFacets.scoring.includes('sufficientScoring')) {
      scoringStr = '&minScoring=121'
      scoringParams = SCORING_RANGES.sufficientScoring
    }
  }

  // --- dataScope override ---
  const NON_COUNTRY_DATA_SCOPES = ['io', 'eu']
  let countryDataParam: Record<string, boolean> = {}

  if (urlQuery.dataScope !== undefined) {
    const isCountryData =
      !NON_COUNTRY_DATA_SCOPES.includes(urlQuery.dataScope) &&
      urlQuery.dataScope === 'countryData'
    countryDataParam = { countryData: isCountryData }
    countryDataStr = isCountryData ? '&countryData=true' : '&countryData=false'
  } else if (urlQuery.countryData !== undefined) {
    const val = urlQuery.countryData === 'true'
    countryDataParam = { countryData: val }
    countryDataStr = val ? '&countryData=true' : '&countryData=false'
  }

  // dataScope non-countryData override (for SearchApiLink)
  const dataScope = urlQuery.dataScope
  if (dataScope?.length && dataScope !== 'countryData') {
    clonedFacets.country = Array.isArray(dataScope) ? dataScope : [dataScope]
    countryDataStr =
      clonedFacets.country.length > 0 ? '&countryData=false' : ''
    countryDataParam = { countryData: false }
  }

  // --- filter string ---
  const filter = config.apiFilter
    ? `&${config.apiFilter}`
    : `&filter=${config.scope}`

  // --- params string (for SearchApiLink URL building) ---
  const params = config.apiQueryParams
    ? `&${config.apiQueryParams}`
    : `${dataServicesParam}${countryDataStr}${scoringStr}`

  // --- readable facets (for SearchApiLink URL building) ---
  const readableFacets = encodeURIComponent(JSON.stringify(clonedFacets))
    .replace(/%3A/g, ':')
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']')
    .replace(/%2C/g, ',')

  const includes = config.apiLinkIncludes ?? null

  return {
    baseUrl,
    urlQuery,
    filter,
    params,
    includes,
    readableFacets,
    facets: clonedFacets,       
    countryDataParam,           
    scoringParams,
  }
}

// Builds the final URL string — used by SearchApiLink 
export function buildSearchApiUrl(extracted: ExtractedParams): string {
  const { baseUrl, urlQuery, filter, params, includes, readableFacets } = extracted
  const includesParam = includes ? `&includes=${includes}` : ''
  const facetsParam = `&facets=${readableFacets}`
  const base = `${baseUrl}search?q=`

  return urlQuery.query
    ? `${base}${urlQuery.query}${filter}${params}${includesParam}${facetsParam}`
    : `${base}${filter}${params}${includesParam}${facetsParam}`
}